import { useState } from "react";
import { toast } from "sonner";
import { Film, ImageIcon, Trash2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { API, apiCall } from "../calendar/calendarUtils";

const CHUNK = 4 * 1024 * 1024;

async function uploadFile(token, exerciseId, kind, file, onProgress) {
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  const uploadId = `${Date.now()}${Math.random().toString(36).slice(2, 10)}`;
  let last;
  for (let i = 0; i < total; i += 1) {
    const fd = new FormData();
    fd.append("upload_id", uploadId); fd.append("index", i); fd.append("total", total);
    fd.append("content_type", file.type); fd.append("filename", file.name);
    fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), "chunk");
    const res = await fetch(`${API}/training/admin/exercises/${exerciseId}/upload/${kind}`, { method: "POST", headers: { "X-Admin-Token": token }, body: fd });
    last = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof last.detail === "string" ? last.detail : "Upload fehlgeschlagen");
    onProgress(Math.round(((i + 1) / total) * 100));
  }
  return last;
}

function Slot({ token, exercise, kind, onChange }) {
  const [pct, setPct] = useState(null);
  const has = kind === "video" ? exercise.video || exercise.video_candidate : exercise[`${kind}_path`];
  const accept = kind === "video" ? "video/mp4,video/quicktime,video/webm" : "image/jpeg,image/png,image/webp";
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPct(0);
    try {
      await uploadFile(token, exercise.exercise_id, kind, file, setPct);
      toast.success(kind === "video" ? "Hochgeladen – Länge und Qualität werden jetzt geprüft" : "Bild hochgeladen");
      onChange(await apiCall(token, `/training/admin/exercises`).then((d) => d.items.find((x) => x.exercise_id === exercise.exercise_id)));
    } catch (err) { toast.error(err.message); }
    setPct(null);
  };
  const remove = async () => {
    await apiCall(token, `/training/admin/exercises/${exercise.exercise_id}/media/${kind}`, { method: "DELETE" });
    onChange({ ...exercise, [`${kind}_path`]: null, [`${kind}_filename`]: null });
  };
  const Icon = kind === "video" ? Film : ImageIcon;
  return (
    <div className="border border-white/10 p-4 space-y-2" data-testid={`media-slot-${kind}`}>
      <p className="flex items-center gap-2 text-sm text-white"><Icon className="w-4 h-4 text-[#c1ff72]" />{kind === "video" ? "Video hochladen (MP4/MOV, 15–20 s, max. 500 MB)" : "Eigenes Bild (JPG/PNG/WebP)"}</p>
      {has && kind !== "video" ? <div className="flex items-center justify-between text-xs text-[#A1A1AA]"><span data-testid={`media-${kind}-name`}>{kind === "video" ? "Vorhanden – Status siehe oben" : exercise[`${kind}_filename`] || "hochgeladen"}</span>
        <button onClick={remove} className="text-red-300 flex items-center gap-1" data-testid={`media-${kind}-remove`}><Trash2 className="w-3 h-3" />Entfernen</button></div>
        : <p className="text-xs text-[#71717A]">{kind === "video" ? "Das neue Video erscheint oben unter „Neues Video“ und muss geprüft werden. Das Kunden-Video bleibt bis zur Freigabe aktiv." : "Noch nichts hochgeladen."}</p>}
      {pct !== null ? <Progress value={pct} className="h-1.5" data-testid={`media-${kind}-progress`} /> : (
        <label className="inline-block text-xs px-3 py-2 border border-white/20 text-white cursor-pointer hover:border-white/50">
          {has ? (kind === "video" ? "Neues Video hochladen" : "Ersetzen") : "Datei wählen"}<input type="file" accept={accept} onChange={pick} className="hidden" data-testid={`media-${kind}-input`} />
        </label>
      )}
    </div>
  );
}

export const MediaUpload = ({ token, exercise, onChange, imageOnly, videoOnly }) => (
  <div className="grid sm:grid-cols-2 gap-3">
    {!imageOnly && <Slot token={token} exercise={exercise} kind="video" onChange={onChange} />}
    {!videoOnly && <Slot token={token} exercise={exercise} kind="image" onChange={onChange} />}
    <p className="sm:col-span-2 text-xs text-[#71717A]">Nur eigene oder lizenzierte Medien hochladen. Videos sind nur für Mitglieder mit passendem Zugang abrufbar.</p>
  </div>
);
